import { UserProfile } from "../../Orchestrator/Recoils/atoms";
import { getRuntimeConfig } from "../Config/RuntimeConfig";
interface SessionData {
  token: string;
  refreshToken?: string;
  isAnonymous: boolean;
  userProfile?: UserProfile;
  idTokenHint: string;
}
export const getSession = async (): Promise<SessionData | null> => {
  const config = await getRuntimeConfig();
  const session = sessionStorage.getItem(config.NEXT_PUBLIC_SESSION_KEY);
  if (session) {
    try {
      return JSON.parse(session);
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  }
  return null;
};

export const setSession = async (data: SessionData): Promise<void> => {
  const config = await getRuntimeConfig();
  sessionStorage.setItem(config.NEXT_PUBLIC_SESSION_KEY, JSON.stringify(data));
};

export const clearSession = async (): Promise<void> => {
  const config = await getRuntimeConfig();
  sessionStorage.removeItem(config.NEXT_PUBLIC_SESSION_KEY);
};

export const getToken = async (): Promise<string | null> => {
  const session = await getSession();
  return session ? session.token + "" : null;
};

/**
 * Whether the current session is an anonymous (guest) session.
 *
 * This is the single source of truth for the guest-vs-logged-in distinction:
 * guests get `{ isAnonymous: true }` and logged-in users `{ isAnonymous: false }`
 * (see useAuth). It is used to suppress the `Authorization` and `requestor`
 * headers (and the `requestor` query param) for guests, while logged-in users
 * keep sending them.
 *
 * Treats "no session yet" as anonymous so we never leak auth/requestor before
 * the session is established.
 */
export const isAnonymousSession = async (): Promise<boolean> => {
  const session = await getSession();
  return session ? session.isAnonymous : true;
};

export const getRefreshToken = async (): Promise<string | null> => {
  const session = await getSession();
  return session ? session.refreshToken + "" : null;
};

export const setAccessToken = async (token: string): Promise<void> => {
  const session = await getSession();
  if (session) {
    session.token = token;
    await setSession(session);
  }
};

export const setRefreshToken = async (token: string): Promise<void> => {
  const session = await getSession();
  if (session) {
    session.refreshToken = token;
    await setSession(session);
  }
};

export const getIdTokenHint = async (): Promise<string | null> => {
  const session = await getSession();
  return session ? session.idTokenHint : null;
};

let cachedUserProfile: UserProfile | null = null;

export const getUserProfile = async (): Promise<UserProfile | null> => {
  if (cachedUserProfile) {
    return cachedUserProfile;
  }

  try {
    const session = await getSession();
    if (session?.userProfile) {
      cachedUserProfile = session.userProfile;
      return cachedUserProfile;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const clearUserProfileCache = (): void => {
  cachedUserProfile = null;
};